import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { HostedTrackingConfig, AdConfig } from "../../ts-interfaces/hosted-tracking-config";
import { OrderInterface, StatusHistory, PersonDetails } from "../../ts-interfaces/order-interface";
import { PickupStatusReverseMapping } from "src/app/shared/constants/pickup-reverse-status";
import { StatusGroupController } from "@deliverysolutions/order-status-mapping";
import { TerminalStatus } from "src/app/shared/constants/terminal-status";
import { SharedService } from "src/app/services/shared.service";
import { SHIP_TO_STORE } from "src/app/shared/constants/hosted-tracking-constants";

@Component({
  selector: "ds-pickup-order-details-new",
  templateUrl: "./pickup-order-details-new.component.html",
  styleUrls: ["./pickup-order-details-new.component.scss"],
})
export class PickupOrderDetailsNewComponent implements OnInit, OnChanges {
  @Input() order!: OrderInterface;
  @Input() hostedTrackingConfig!: HostedTrackingConfig;
  @Input() isSystemStatus!: boolean;
  @Input() isTerminalStatus!: boolean;
  @Input() showSiblingOrders!: boolean;
  @Output() openDeliveryProofsModal: EventEmitter<boolean> = new EventEmitter();

  public primaryAds: AdConfig | undefined;
  statusHistory: StatusHistory[] = [];

  public isDriverInfoAvailable = false;
  public customerDetails: PersonDetails = { name: "", phone: "", email: "" };
  public driverDetails: PersonDetails = { name: "", phone: "", email: "" };

  private statusGroup = new StatusGroupController();

  private labels = this.sharedService.getInitialLabels();

  public pickupHeading = this.labels.pickupDate;
  public pickupDate = "";
  public pickupStatus = "";
  public statusIcon = "fa-regular fa-bell";

  constructor(private sharedService: SharedService) {}

  ngOnInit(): void {
    // primary ad is mandatory
    this.primaryAds = this.hostedTrackingConfig.components.ads.find((ads) => {
      return ads.active && ads.type === "primary";
    });

    this.getPickupCustomerInfo();
    this.updateOrderStatusData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.order && changes.order.currentValue) {
      this.updateOrderStatusData();
      this.getPickupCustomerInfo();
    }
  }

  getPickupCustomerInfo() {
    if (this.order.driver && this.order.driver?.name) {
      this.isDriverInfoAvailable = true;

      this.driverDetails = {
        name: this.order.driver.name ?? "",
        phone: this.order.driver.phone ? this.sharedService.cleanPhoneNumber(this.order.driver.phone) : "",
        email: "",
      };
    } else {
      this.isDriverInfoAvailable = false;
    }

    if (this.order.deliveryContact) {
      this.customerDetails = {
        name: this.order.deliveryContact.name ?? "",
        phone: this.order.deliveryContact.phone
          ? this.sharedService.cleanPhoneNumber(this.order.deliveryContact.phone)
          : "",
        email: this.order.deliveryContact.email ?? "",
      };
    }
  }

  // Updates pickup heading, date, and status string based on order status and timestamps
  private updateOrderStatusData(): void {
    if (TerminalStatus.includes(this.order.status)) {
      this.handleTerminalStatus();
    } else {
      this.handleNonTerminalStatus();
    }
  }

  private handleTerminalStatus(): void {
    const statusIndex = this.order.statusHistory.findIndex((entry) => entry.status === this.order.status);

    // if status Index == -1 , mark the status as Picked up with current timing
    // as this information is missing from the statusHistory
    let updatedAt;
    if (statusIndex === -1) {
      updatedAt = Date.now();
    } else {
      updatedAt = this.order.statusHistory[statusIndex]?.updatedAtEpoch;
    }

    const timeObj = this.sharedService.generateTimeToShow(updatedAt, this.order);
    if (!timeObj) return;

    const label = this.getPickupStatus(this.order.status);

    this.pickupDate = `${timeObj.weekday.substring(0, 3)}, ${timeObj.month.substring(0, 3)} ${timeObj.day}`;
    this.pickupStatus = `${label} ${this.labels.atText} ${timeObj.time}`;
    this.statusIcon = "fa-regular fa-circle-check";
  }

  // Handles logic for non-terminal orders (e.g., Ready for Pickup)
  private handleNonTerminalStatus(): void {
    const formatDate = (pickup: any): string =>
      `${pickup.weekday.substring(0, 3)}, ${pickup.month.substring(0, 3)} ${pickup.day}`;

    const formatTimeRange = (start: any, end: any): string =>
      `${start.weekday.substring(0, 3)}, ${start.month.substring(0, 3)} ${start.day}, ${start.time} - ` +
      `${end.weekday.substring(0, 3)}, ${end.month.substring(0, 3)} ${end.day}, ${end.time}`;

    const formatSameDayRange = (start: any, end: any): string =>
      `${start.weekday.substring(0, 3)}, ${start.month.substring(0, 3)} ${start.day}, ${this.labels.betweenText} ` +
      `${start.time} - ${end.time}`;

    const { estimatedPickupTime, pickupTime, createdAt } = this.order;

    const currentStatus = this.getPickupStatus(this.order.status);

    if (this.order.status === SHIP_TO_STORE["in-transit-status"]) {
      this.pickupStatus = currentStatus;
      this.statusIcon = "fa-solid fa-truck";
      if (this.order.estimatedDeliveryTime) {
        const pickupTime = this.sharedService.generateTimeToShow(this.order.estimatedDeliveryTime, this.order);
        this.pickupDate = formatDate(pickupTime);
      } else if (!this.order.estimatedDeliveryTime && this.order.dropoffTime?.endsAt) {
        const dropOffTime = this.order.dropoffTime?.endsAt;
        const pickupTime = this.sharedService.generateTimeToShow(dropOffTime, this.order);
        this.pickupDate = formatDate(pickupTime);
      } else {
        this.pickupDate = this.labels.unavailableText;
      }
    } else {
      this.statusIcon = "fa-regular fa-bell";

      if (estimatedPickupTime) {
        const start = this.sharedService.generateTimeToShow(estimatedPickupTime, this.order);
        if (start) {
          this.pickupDate = formatDate(start);
          this.pickupStatus = `${currentStatus} ${this.labels.afterText} ${start.time}`;
        }
        return;
      }

      if (pickupTime?.startsAt && pickupTime?.endsAt) {
        const start = this.sharedService.generateTimeToShow(pickupTime.startsAt, this.order);
        const end = this.sharedService.generateTimeToShow(pickupTime.endsAt, this.order);

        if (start && end) {
          const sameDay = start.day === end.day && start.month === end.month;
          this.pickupDate = sameDay ? formatSameDayRange(start, end) : formatTimeRange(start, end);
          this.pickupStatus = currentStatus;
          this.pickupHeading = sameDay ? this.labels.pickupDate : this.labels.pickupBetween;
        }
        return;
      }

      if (pickupTime?.startsAt) {
        const start = this.sharedService.generateTimeToShow(pickupTime.startsAt, this.order);
        if (start) {
          this.pickupDate = formatDate(start);
          this.pickupStatus = `${currentStatus} ${this.labels.afterText} ${start.time}`;
        }
        return;
      }

      if (createdAt) {
        const start = this.sharedService.generateTimeToShow(new Date(createdAt).getTime(), this.order);
        if (start) {
          this.pickupDate = formatDate(start);
          this.pickupStatus = `${currentStatus} ${this.labels.afterText} ${start.time}`;
        }
      }
    }
  }

  getPickupStatus(status: string): string {
    const experienceType = (this.order?.orderAttributes?.experienceType || this.order?.type || "") as string;
    return status ? this.sharedService.getTranslatedStatus(experienceType, status) : "";
  }
}
